// SensorPanel.jsx — Sensor configuration grid with sparklines
// Extracted from HypothesisExplorer for modularity.
// This is the section that changes when wiring social_service (8300).
//
// Usage: <SensorPanel sensors={detail.sensors} color={detail.color}
//           isLive={detail.status === "done"} genomaUI={GENOMA_UI} />

import {
  AreaChart, Area, ResponsiveContainer, ReferenceLine,
} from "recharts";

// ── Signal style helper ───────────────────────────────────────────────────
const C = {
  red: "#f03e3e", green: "#34d399", blue: "#60a5fa",
  amber: "#fbbf24", white: "#e8e8f0",
  dim: "rgba(232,232,240,0.80)", dimmer: "rgba(232,232,240,0.62)",
};

function signalStyle(status, signal) {
  if (status === "AGUARDANDO") return { bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.35)", color: C.amber, label: "AGUARDANDO", icon: "◌" };
  if (status === "STALE") return { bg: "rgba(120,113,108,0.14)", border: "rgba(120,113,108,0.35)", color: "#a3a3a3", label: "STALE", icon: "◆" };
  if (signal === "OVER") return { bg: "rgba(240,62,62,0.14)", border: "rgba(240,62,62,0.40)", color: C.red, label: "FIRING", icon: "●" };
  if (signal === "APPROACHING") return { bg: "rgba(251,191,36,0.14)", border: "rgba(251,191,36,0.40)", color: C.amber, label: "PRÓXIMO", icon: "▲" };
  if (signal === "UNDER") return { bg: "rgba(52,211,153,0.10)", border: "rgba(52,211,153,0.30)", color: C.green, label: "LIVE", icon: "●" };
  return { bg: "rgba(96,165,250,0.10)", border: "rgba(96,165,250,0.30)", color: C.blue, label: "LIVE", icon: "●" };
}

function fmtValue(v, decimals = 2) {
  if (v == null || isNaN(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(decimals)}`;
}

// ── Sparkline ─────────────────────────────────────────────────────────────
function SensorSparkline({ data, color, threshold }) {
  if (!data || data.length < 3) {
    return <div style={{ height: 56, display: "flex", alignItems: "center", color: C.dimmer, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.06em" }}>SEM DADOS</div>;
  }
  const gradId = `spark-${color.replace("#","")}-${Math.random().toString(36).slice(2,7)}`;
  const threshNum = threshold != null ? parseFloat(threshold) : null;
  return (
    <div style={{ height: 56, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.55} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          {threshNum != null && !isNaN(threshNum) && (
            <ReferenceLine y={threshNum} stroke={C.red} strokeDasharray="3 2" strokeWidth={1} strokeOpacity={0.5} />
          )}
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={1.8} fill={`url(#${gradId})`} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────
export default function SensorPanel({ sensors, color, isLive, genomaUI = "" }) {
  if (!sensors || sensors.length === 0) return null;

  return (
    <div style={{ padding: "22px 26px 8px", position: "relative" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: "0.14em", marginBottom: 6 }}>CONFIGURAÇÃO DE SENSORES</div>
      <p style={{
        fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic", fontSize: 13,
        color: C.dim, margin: "0 0 16px",
      }}>
        {isLive
          ? "Leituras em tempo real do backbone Genoma. Cada sensor cruza um limiar operacional distinto."
          : "Pipeline em construção. Sensores descrevem a arquitetura pretendida — a integração com dados reais é backlog."}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {sensors.map((s, i) => {
          const st = signalStyle(s.reading?.status, s.reading?.signal);
          const pulse = s.reading?.signal === "OVER";
          const threshVal = s.threshold ? parseFloat(String(s.threshold).replace(/[^0-9.\-]/g, "")) : null;
          return (
            <div key={i} className="rr-hyp-sensor-row" style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1.5fr) minmax(0, 0.9fr) auto",
              gap: 16, padding: "16px 18px",
              background: "rgba(255,255,255,0.02)", border: "1px solid #1e1e2a",
              borderRadius: 6, alignItems: "center",
            }}>
              {/* Name + cluster link */}
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, fontWeight: 600,
                  color: C.white, letterSpacing: "-0.005em", marginBottom: 5, lineHeight: 1.25,
                }}>{s.name}</div>
                {s.genoma_cluster ? (
                  <a href={`${genomaUI}/magma/genoma/${s.genoma_cluster}`} className="rr-hyp-genoma-link" target="_blank" rel="noopener noreferrer" style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                    color, letterSpacing: "0.03em", textDecoration: "none",
                  }}>↗ genoma · {s.genoma_cluster}</a>
                ) : (
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                    color: C.dimmer, letterSpacing: "0.03em",
                  }}>{s.note}</div>
                )}
              </div>

              {/* Sparkline */}
              <div>
                <SensorSparkline data={s.reading?.sparkline} color={st.color} threshold={threshVal} />
              </div>

              {/* Value + threshold */}
              <div>
                {s.reading?.status === "LIVE" ? (
                  <>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 18,
                      fontWeight: 700, color: st.color, marginBottom: 3,
                    }}>{fmtValue(s.reading.value, 1)}{s.reading.unit}</div>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                      color: C.dimmer, letterSpacing: "0.03em",
                    }}>alvo · {s.threshold}</div>
                    {s.reading.yoy != null && (
                      <div style={{
                        display: "inline-block", marginTop: 4,
                        padding: "2px 7px", borderRadius: 3,
                        background: s.reading.yoy > 0 ? "rgba(52,211,153,0.12)" : "rgba(240,62,62,0.12)",
                        color: s.reading.yoy > 0 ? C.green : C.red,
                        fontSize: 10, fontWeight: 700,
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}>YoY {fmtValue(s.reading.yoy, 1)}%</div>
                    )}
                  </>
                ) : (
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                    color: C.dim, letterSpacing: "0.03em", lineHeight: 1.4,
                  }}>alvo · {s.threshold}</div>
                )}
              </div>

              {/* Status pill */}
              <span className={pulse ? "rr-hyp-firing" : ""} style={{
                padding: "7px 14px", borderRadius: 4,
                background: st.bg, border: `1px solid ${st.border}`,
                color: st.color, fontSize: 11, fontWeight: 700,
                letterSpacing: "0.1em", whiteSpace: "nowrap",
              }}>
                {st.icon} {st.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
